import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { authApi } from "../lib/auth";

const AuthContext = createContext(null);

// session: undefined = checking, null = signed out, { user, org } = signed in
export function AuthProvider({ children }) {
  const [session, setSession] = useState(undefined);

  const refresh = useCallback(async () => {
    try {
      setSession(await authApi.me());
    } catch {
      setSession(null);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      session,
      user: session?.user || null,
      org: session?.org || null,
      isAdmin: session?.user?.role === "admin",
      setSession,
      refresh,
      logout: async () => {
        try {
          await authApi.logout();
        } finally {
          setSession(null);
        }
      },
    }),
    [session, refresh]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default function useAuth() {
  return useContext(AuthContext);
}

export function RequireAuth({ children, admin = false }) {
  const { session, user } = useAuth();
  const location = useLocation();
  if (session === undefined) {
    return (
      <div className="auth-checking" data-testid="auth-checking">
        Checking your session...
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (user.must_change_password && location.pathname !== "/app/change-password") return <Navigate to="/app/change-password" replace />;
  if (session.org?.require_mfa && !user.mfa_enabled && location.pathname !== "/app/settings") return <Navigate to="/app/settings" replace state={{ mfaRequired: true }} />;
  if (admin && user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
}
